import React, { forwardRef, useState, useEffect, useCallback } from 'react';
import {
  OfferTemplateConfig,
  DEFAULT_TEMPLATE_CONFIG,
  DEFAULT_LAYOUT_CONFIG,
  TemplateLayoutConfig,
  TemplatePage,
} from '../../../types/offerTemplate.types';
import PageRendererFactory from './templateBlocks/renderers/PageRendererFactory';
import CanvasPageRenderer, { PlaceholderData } from './canvasEditor/CanvasPageRenderer';
import { STAGE_WIDTH, STAGE_HEIGHT } from './canvasEditor/canvasElements';

export interface BranchInfoData {
  name: string;
  taxNumber?: string;
  taxOffice?: string;
  address?: string;
  phone?: string;
  email?: string;
  mersisNo?: string;
  tradeRegistryNo?: string;
  bankAccounts?: Array<{ bankName: string; iban: string }>;
}

export interface OfferItemData {
  id: string;
  name: string;
  itemName?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  vatRate: number;
  sctRate?: number;
  discountRate?: number;
  totalPrice: number;
  grossTotal?: number;
}

export interface GivenOfferData {
  id: string;
  offerNumber: string;
  accountId: string;
  account: {
    companyName: string;
    taxNumber?: string;
    taxOffice?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  date: string;
  validUntil: string;
  subtotal: number;
  vatTotal: number;
  sctTotal: number;
  discountTotal: number;
  additionalDiscountAmount?: number;
  discountType?: 'percentage' | 'amount' | null;
  discountValue?: number | null;
  tevkifatRate?: string;
  tevkifatAmount?: number;
  stopajRate?: number;
  stopajAmount?: number;
  grandTotal: number;
  status: string;
  currency: string;
  createdAt: string;
  updatedAt?: string;
  items?: OfferItemData[];
  notes?: string;
}

export interface ReceivedOfferData {
  id: string;
  offerNumber: string;
  supplierId: string;
  supplier: {
    companyName: string;
    taxNumber?: string;
    taxOffice?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  date: string;
  validUntil: string;
  amount: number;
  subtotal?: number;
  vatTotal?: number;
  sctTotal?: number;
  discountTotal?: number;
  additionalDiscountAmount?: number;
  discountType?: 'percentage' | 'amount' | null;
  discountValue?: number | null;
  grandTotal?: number;
  status: string;
  currency: string;
  createdAt: string;
  updatedAt?: string;
  items?: OfferItemData[];
  notes?: string;
}

type OfferData = GivenOfferData | ReceivedOfferData;

interface OfferPdfTemplateProps {
  config?: OfferTemplateConfig;
  companyInfo?: BranchInfoData | null;
  offer: OfferData;
  offerType: 'given' | 'received';
}

const CanvasPageImage: React.FC<{ page: TemplatePage; placeholderData?: PlaceholderData }> = ({ page, placeholderData }) => {
  const [imgSrc, setImgSrc] = useState<string>('');
  const elements = Array.isArray(page.settings?.canvasElements) ? page.settings.canvasElements : [];
  const bgColor = page.settings?.bgColor || '#ffffff';

  const handleReady = useCallback((dataUrl: string) => { setImgSrc(dataUrl); }, []);

  return (
    <>
      <CanvasPageRenderer elements={elements} bgColor={bgColor} onImageReady={handleReady} placeholderData={placeholderData} />
      {imgSrc ? (
        <div data-pdf-page="true" style={{ width: STAGE_WIDTH, height: STAGE_HEIGHT }}>
          <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', display: 'block' }} />
        </div>
      ) : (
        <div data-pdf-page="true" style={{ width: STAGE_WIDTH, height: STAGE_HEIGHT, backgroundColor: bgColor }} />
      )}
    </>
  );
};

const isCanvasPageType = (page: TemplatePage) => page.type === 'coverPage' || page.type === 'contentPage';

function buildPlaceholderData(offer: OfferData, offerType: 'given' | 'received', companyInfo?: BranchInfoData | null): PlaceholderData {
  const isGiven = offerType === 'given';
  const g = isGiven ? (offer as GivenOfferData) : undefined;
  const r = !isGiven ? (offer as ReceivedOfferData) : undefined;
  const account = g?.account || r?.supplier;
  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('tr-TR') : '';
  const formatCurrency = (n?: number) => n !== undefined ? n.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : '';

  return {
    müşteriAdı: account?.companyName || '',
    müşteriVergiNo: account?.taxNumber || '',
    müşteriVergiDairesi: account?.taxOffice || '',
    müşteriAdres: account?.address || '',
    müşteriTelefon: account?.phone || '',
    müşteriEmail: account?.email || '',
    teklifNo: offer.offerNumber || '',
    teklifTarihi: formatDate(offer.date),
    gecerlilikTarihi: formatDate((offer as any).validUntil),
    firmaAdı: companyInfo?.name || '',
    firmaTelefon: companyInfo?.phone || '',
    firmaEmail: companyInfo?.email || '',
    firmaAdres: companyInfo?.address || '',
    genelToplam: formatCurrency(g?.grandTotal ?? r?.grandTotal),
    paraBirimi: offer.currency || 'TRY',
    kdvToplam: formatCurrency(g?.vatTotal ?? r?.vatTotal),
    araToplam: formatCurrency(g?.subtotal ?? r?.subtotal),
  };
}

const OfferPdfTemplate = forwardRef<HTMLDivElement, OfferPdfTemplateProps>(
  ({ config: configProp, companyInfo, offer, offerType }, ref) => {
    const config = configProp || DEFAULT_TEMPLATE_CONFIG;
    const layout: TemplateLayoutConfig = config.layoutConfig || DEFAULT_LAYOUT_CONFIG;
    const pData = buildPlaceholderData(offer, offerType, companyInfo);

    const visiblePages = [...layout.pages]
      .filter(p => p.visible)
      .sort((a, b) => a.order - b.order);

    return (
      <div ref={ref} style={{ fontFamily: `'${config.font}', Arial, sans-serif`, fontSize: `${config.fontBoyutu}px` }}>
        {visiblePages.map((page, idx) => {
          const hasCanvasData = isCanvasPageType(page) && Array.isArray(page.settings?.canvasElements) && page.settings.canvasElements.length > 0;

          return (
            // key şablon id'sini de içerir: farklı şablonlar aynı sayfa id'lerini
            // (page-cover vb.) paylaşabildiğinden, şablon değişiminde bileşenin
            // yeniden kurulup bayat kanvas görüntüsünün atılması gerekir
            <div key={`${config.id || 'tpl'}-${page.id}`} style={{ pageBreakAfter: idx < visiblePages.length - 1 ? 'always' : 'auto' }}>
              {hasCanvasData ? (
                <CanvasPageImage page={page} placeholderData={pData} />
              ) : (
                <PageRendererFactory
                  page={page}
                  config={config}
                  offer={offer}
                  offerType={offerType}
                  companyInfo={companyInfo}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }
);

OfferPdfTemplate.displayName = 'OfferPdfTemplate';

export default OfferPdfTemplate;
